'use client'

import { useState, type FC } from 'react'
import type { NavItemProps } from './types'
import clsx from 'clsx'
import { Typography } from '../Typography'
import Link from 'next/link'

export const NavItem: FC<NavItemProps> = ({
  testId = 'nav-item',
  icon,
  iconTrailing,
  labelTrailing,
  title,
  isSelected = false,
  href,
  newTab = false,
  onClick,
  textClassName,
}) => {
  const isExternalLink = href && /^https?:\/\//.test(href)

  const [isHovered, setIsHovered] = useState(false)

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  const linkContent = (
    <>
      <span data-testid={`${testId}-icon`} className="h-5 w-5 shrink-0">
        {icon}
      </span>

      <Typography
        testId={`${testId}-title`}
        size="sm"
        weight="semibold"
        className={clsx(
          'text-left text-inherit whitespace-nowrap',
          textClassName,
          {
            truncate: !isHovered, // Truncate by default
            'whitespace-normal max-w-full': isHovered, // Expand on hover
          },
        )}
      >
        {title}
      </Typography>

      {iconTrailing && (
        <span
          data-testid={`${testId}-icon-trailing`}
          className="ml-auto h-4 w-4 shrink-0"
        >
          {iconTrailing}
        </span>
      )}

      {labelTrailing && !isHovered && (
        <span
          data-testid={`${testId}-label-trailing`}
          className="ml-auto shrink-0"
        >
          {labelTrailing}
        </span>
      )}
    </>
  )

  const className = clsx(
    'flex gap-x-3 h-10 w-full cursor-pointer items-center border-l-4 hover:bg-default-hover px-3',
    {
      'text-default border-transparent hover:border-subtle-hover': !isSelected,
      'px-3 gap-x-3 text-content-action-default border-content-action-default':
        isSelected,
    },
  )

  return isExternalLink ? (
    <a
      data-testid={testId}
      href={href}
      target={newTab ? '_blank' : '_self'}
      rel={newTab ? 'noopener noreferrer' : undefined}
      className={className}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {linkContent}
    </a>
  ) : (
    <Link
      data-testid={testId}
      href={href}
      className={className}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {linkContent}
    </Link>
  )
}
